// Fleet's page (components/FleetOverview.jsx): what runs in the fleet as one
// inventory, started from whichever kind the reader picks (agents, models,
// providers, endpoints or local inference), each row against
// the span before. A row carries what it touches (`rel`), so its panel can
// start the inventory from any of that, and the words a search looks through
// (`terms`). Pure: the reads are FleetList's block (agents, models and
// providers), readFleetLocal (local inference) and a page of the
// endpoints (getFleetEndpoints).
import { installedKey, isModelBlob, isRuntimeProduct, modelKey, modelLabel, runtimeOf } from "./fleetConsole.js";
import { endpointRowOf } from "./fleetCoverage.js";
import { beforeOf, rowsOf, unusedRows } from "./fleetList.js";
import { listed } from "./fleetLocal.js";
import { KINDS, providerName } from "./fleetBreakdown.js";

export const TABS = [["agents", "Agents"], ["models", "Models"], ["providers", "Providers"], ["endpoints", "Endpoints"], ["runtimes", "Local inference"]];
export const TAB_IDS = TABS.map(([id]) => id);

// Each tab's columns after the name and the bar: what a cell shows, and
// whether its header sorts. The endpoints are sorted by the server, which
// orders by name or traces only.
export const COLUMNS = {
  agents: { name: "Agent", cells: [["now", "Traces"], ["change", "Change"], ["models", "Models"], ["installed", "Installed"]] },
  models: { name: "Model", cells: [["now", "Traces"], ["change", "Change"], ["agents", "Agents"]] },
  providers: { name: "Provider", cells: [["now", "Traces"], ["change", "Change"], ["models", "Models"], ["agents", "Agents"]] },
  endpoints: { name: "Endpoint", cells: [["now", "Traces"], ["change", "Change", false], ["agents", "Agents", false], ["heard", "Last heard", false]] },
  runtimes: { name: "Local model or runtime", cells: [["now", "Endpoints"], ["change", "Change"], ["traces", "Traces"], ["installed", "Installed"]] },
};
export const sortable = (tab, key) => key === "name" || COLUMNS[tab].cells.some(([id, , sorts = true]) => id === key && sorts);
// A list is sorted by its first number, the most first, until a header is picked.
export const DEFAULT_SORT = { key: "now", dir: "desc" };

const STATE = { fresh: "new", idle: "installed, not used", quiet: "not heard from" };
const lower = (list) => list.filter(Boolean).join("\n").toLowerCase();
const byNow = (a, b) => (b.now ?? -1) - (a.now ?? -1) || a.name.localeCompare(b.name);
// Something in the range with none in the span before is new; unknown, and so not new, without one.
const stateOf = (now, before, installed = 0) => (before === 0 && now > 0 ? STATE.fresh : !now && installed > 0 ? STATE.idle : "");
// The kinds an agent ran in, the most traces first: "Hybrid and web chat".
const kindsText = (parts = []) => listed(parts.map((id, i) => (i ? KINDS[id].toLowerCase() : KINDS[id])));
// Something a row touches: the tab it is listed in, its id there, its name,
// its traces (or none), and what to search that tab for (`q`, its name unless
// the name says more, as a version does).
const item = (kind, id, name, n = null, q = name) => ({ kind, id, name, n, q });
// The providers that served a row, as the Providers tab lists them; a local model's is Local inference's.
const providersOf = (tags = []) => [...new Set(tags)].filter((t) => t && t !== "local").sort().map((t) => item("providers", t, providerName(t)));

/**
 * The agents of the range and of the span before, and those installed and
 * never used in the range: each with its traces now and before (null where
 * there is no span before), the models it called and how many endpoints have
 * it installed (null where the inventory was not read).
 */
export function agentRows(d) {
  if (!d?.now) return [];
  const installed = d.installed || null;
  const was = beforeOf("agents", d.then);
  const now = rowsOf("agents", d.now, null, installed);
  const seen = new Set(now.map((r) => r.id));
  const gone = d.then ? rowsOf("agents", d.then, null, installed).filter((r) => !seen.has(r.id)).map((r) => ({ ...r, sessions: 0, used: [], parts: [] })) : [];
  for (const r of gone) seen.add(r.id);
  const idle = unusedRows(installed, d.now).filter((r) => !seen.has(r.id));
  return [...now, ...gone, ...idle].map((r) => {
    const before = was ? was.get(r.id)?.sessions ?? 0 : null;
    const models = r.used.map((u) => item("models", u.id, u.name, u.sessions));
    const providers = (r.providers || []).map(providerName);
    const state = stateOf(r.sessions, before, r.installed ?? 0);
    return {
      kind: "agents", id: r.id, name: r.name, sub: kindsText(r.parts), state,
      now: r.sessions, before, installed: r.installed ?? null, counts: { models: models.length },
      rel: [{ kind: "models", label: "Models", items: models }, { kind: "providers", label: "Providers", items: providersOf(r.providers) }],
      terms: lower([r.name, kindsText(r.parts), state, ...providers, ...models.map((m) => m.name)]),
    };
  }).sort(byNow);
}

/** The models of the range and of the span before: each with its traces, the providers that served it, and the agents that called it. */
export function modelRows(d) {
  if (!d?.now) return [];
  const was = beforeOf("models", d.then);
  const now = rowsOf("models", d.now);
  const seen = new Set(now.map((r) => r.id));
  const gone = d.then ? rowsOf("models", d.then).filter((r) => !seen.has(r.id)).map((r) => ({ ...r, sessions: 0, used: [] })) : [];
  return [...now, ...gone].map((r) => {
    const before = was ? was.get(r.id)?.sessions ?? 0 : null;
    const agents = r.used.map((u) => item("agents", u.id, u.name, u.sessions));
    const sub = listed([...new Set((r.providers || []).map(providerName))].sort());
    const state = stateOf(r.sessions, before);
    return {
      kind: "models", id: r.id, name: r.name, sub, state, now: r.sessions, before, counts: { agents: agents.length },
      rel: [{ kind: "agents", label: "Agents", items: agents }, { kind: "providers", label: "Providers", items: providersOf(r.providers) }],
      terms: lower([r.name, sub, state, ...(r.spellings || []), ...agents.map((a) => a.name)]),
    };
  }).sort(byNow);
}

// A block's traces by provider: each trace has one, so its
// groups (each trace once, as the Overview counts it) add up to it exactly.
// No provider, and local models (Local inference's), are left out.
function byProvider(block) {
  const by = new Map();
  const at = (tag) => {
    if (!by.has(tag)) by.set(tag, { sessions: 0, kinds: new Map(), agents: new Map(), models: new Map() });
    return by.get(tag);
  };
  const add = (map, id, name, n) => map.set(id, { id, name, n: (map.get(id)?.n || 0) + n });
  for (const g of block?.groups || []) {
    if (!g.provider || g.provider === "local") continue;
    const p = at(g.provider);
    p.sessions += g.sessions;
    p.kinds.set(g.kind, (p.kinds.get(g.kind) || 0) + g.sessions);
    add(p.agents, g.agent, g.agentName, g.sessions);
  }
  for (const l of block?.links || []) {
    if (!l.provider || l.provider === "local" || !l.model) continue;
    add(at(l.provider).models, l.model, l.modelName, l.sessions);
  }
  return by;
}

/** The providers of the range and of the span before: each with its traces, how they reached it, and the models and agents it served. */
export function providerRows(d) {
  if (!d?.now) return [];
  const now = byProvider(d.now);
  const then = d.then ? byProvider(d.then) : null;
  const list = (map, kind) => [...map.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name)).map((x) => item(kind, x.id, x.name, x.n));
  return [...new Set([...now.keys(), ...(then?.keys() || [])])].map((tag) => {
    const p = now.get(tag) || { sessions: 0, kinds: new Map(), agents: new Map(), models: new Map() };
    const before = then ? then.get(tag)?.sessions ?? 0 : null;
    const sub = kindsText([...p.kinds].sort((a, b) => b[1] - a[1]).map(([k]) => k));
    const models = list(p.models, "models");
    const agents = list(p.agents, "agents");
    const state = stateOf(p.sessions, before);
    return {
      kind: "providers", id: tag, name: providerName(tag), sub, state, now: p.sessions, before, counts: { models: models.length, agents: agents.length },
      rel: [{ kind: "models", label: "Models", items: models }, { kind: "agents", label: "Agents", items: agents }],
      terms: lower([providerName(tag), tag, sub, state, ...models.map((m) => m.name), ...agents.map((a) => a.name)]),
    };
  }).sort(byNow);
}

/**
 * A page of the endpoints (getFleetEndpoints rows, in the server's order)
 * with the agents that made their traces (getFleetEndpointAgents paths), their
 * traces in the span before (lib/fleetCoverage.js endpointsBefore, null
 * without one), when each was last heard from, and which were heard from at
 * all in the range (`heard`, null where unknown).
 */
export function endpointRows(page = [], { agents = [], before = null, lastSeen = new Map(), heard = null } = {}) {
  return page.map((p) => {
    const r = endpointRowOf(p, agents);
    const inv = r.inventory || {};
    const sub = [inv.platform, inv.osDisplayVersion].filter(Boolean).join(" ");
    const installed = (inv.installedAiAgentsList || []).filter((a) => a?.name);
    const quiet = heard ? !heard.has(r.id) : false;
    const was = before ? before.get(r.id)?.sessions ?? 0 : null;
    const state = quiet ? STATE.quiet : stateOf(r.sessions, was);
    const agentItems = r.used.map((u) => item("agents", u.id, u.name, u.sessions));
    const runtimes = installed.filter((a) => isRuntimeProduct(a.name)).map((a) => item("runtimes", `runtime:${installedKey(a.name)}`, a.name));
    const products = installed.filter((a) => !isRuntimeProduct(a.name)).map((a) => item("agents", installedKey(a.name), a.version ? `${a.name} ${a.version}` : a.name, null, a.name));
    return {
      kind: "endpoints", id: r.id, name: r.name, sub, state, now: r.sessions, before: was,
      heard: lastSeen.get(r.id) ?? null, counts: { agents: agentItems.length },
      rel: [
        { kind: "agents", label: "Agents", items: agentItems },
        { kind: "agents", label: "Installed", items: products },
        { kind: "runtimes", label: "Local runtimes", items: runtimes },
      ],
      terms: lower([r.name, sub, state, ...agentItems.map((a) => a.name), ...installed.map((a) => a.name)]),
    };
  });
}

// Endpoints by key from rows of { endpoint, ... }, each key's endpoints and
// the traces (`sessions`) on each.
function onEndpoints(rows, keyOf) {
  const by = new Map();
  for (const r of rows || []) {
    const key = keyOf(r);
    const ep = String(r.endpoint || "");
    if (!key || !ep) continue;
    if (!by.has(key.id)) by.set(key.id, { name: key.name, on: new Map() });
    const at = by.get(key.id).on;
    at.set(ep, (at.get(ep) || 0) + (Number(r.sessions) || 0));
  }
  return by;
}

/**
 * Local inference from its read (readFleetLocal, with the span before's): the
 * models endpoints ran themselves, by the endpoints that ran each and their
 * traces; and the runtimes, by the endpoints seen running each (null without
 * `tenant.events.read`) and those with it installed. Endpoints are the
 * measure, since a runtime's launch is not a trace.
 */
export function localRows(d) {
  if (!d?.now) return [];
  const host = new Map((d.names || []).map((n) => [String(n.id), String(n.hostname || "") || "Unnamed endpoint"]));
  const endpoints = (on) => [...on].map(([id, n]) => item("endpoints", id, host.get(id) || "Unnamed endpoint", n || null)).sort((a, b) => (b.n || 0) - (a.n || 0) || a.name.localeCompare(b.name));
  const model = (r) => (r.model ? { id: modelKey(r.model), name: String(r.model) } : null);
  // A model named by its file shows that file's name, and keeps the whole value (`full`) to copy and search by.
  const named = (raw) => (raw && modelLabel(raw) !== raw ? { name: modelLabel(raw), full: raw, sub: isModelBlob(raw) ? "Local model, named by its file in Ollama's model store" : "Local model" } : { name: raw, sub: "Local model" });
  const nowModels = onEndpoints(d.now, model);
  const thenModels = d.then ? onEndpoints(d.then, model) : null;
  const models = [...new Set([...nowModels.keys(), ...(thenModels?.keys() || [])])].map((id) => {
    const cur = nowModels.get(id);
    const was = thenModels?.get(id);
    const on = endpoints(cur?.on || new Map());
    const now = on.length;
    const before = thenModels ? was?.on.size ?? 0 : null;
    const state = stateOf(now, before);
    return {
      kind: "runtimes", id: `model:${id}`, ...named(cur?.name || was?.name), state,
      now, before, traces: cur ? [...cur.on.values()].reduce((n, k) => n + k, 0) : 0, installed: null,
      rel: [{ kind: "endpoints", label: "Endpoints", items: on }],
      terms: lower(["local model", cur?.name || was?.name, modelLabel(cur?.name || was?.name), state, ...on.map((e) => e.name)]),
    };
  });
  // A runtime its launch does not name is written as Local inference's list writes it (lib/fleetLocal.js).
  const runtime = (r) => {
    const rt = runtimeOf(r);
    return rt ? { id: rt.key, name: rt.key === "local" ? "Unnamed runtime" : rt.name } : null;
  };
  const ran = d.ran ? onEndpoints(d.ran.map((e) => ({ ...e, sessions: 0 })), runtime) : null;
  const ranThen = d.ranThen ? onEndpoints(d.ranThen.map((e) => ({ ...e, sessions: 0 })), runtime) : null;
  const installedOn = onEndpoints((d.installed || []).filter((i) => i.name).map((i) => ({ ...i, sessions: 0 })), (i) => ({ id: installedKey(i.name), name: String(i.name) }));
  const runtimes = [...new Set([...(ran?.keys() || []), ...installedOn.keys()])].map((key) => {
    const on = endpoints(ran?.get(key)?.on || new Map());
    const inst = endpoints(installedOn.get(key)?.on || new Map());
    const name = ran?.get(key)?.name || installedOn.get(key)?.name;
    const now = ran ? on.length : null;
    const before = ran && ranThen ? ranThen.get(key)?.on.size ?? 0 : null;
    const state = now == null ? "" : stateOf(now, before, inst.length);
    return {
      kind: "runtimes", id: `runtime:${key}`, name, sub: "Runtime", state, now, before, traces: null, installed: d.installed ? inst.length : null,
      rel: [{ kind: "endpoints", label: "Ran on", items: on }, { kind: "endpoints", label: "Installed on", items: inst }],
      terms: lower(["runtime", name, state, ...on.map((e) => e.name), ...inst.map((e) => e.name)]),
    };
  });
  return [...models, ...runtimes].sort(byNow);
}

/** Whether every word of a search (lib/findingsConsole.js searchWords) is in what a row looks through. */
export const matches = (row, words) => words.every((w) => row.terms.includes(w));

/**
 * Where a search matched something other than a row's name or line under it,
 * what it matched: the first thing it touches whose name holds a word, as
 * { label, name }; null where the name or line holds them all.
 */
export function matchedVia(row, words) {
  const own = `${row.name}\n${row.sub}\n${row.state}`.toLowerCase();
  if (!words.length || words.every((w) => own.includes(w))) return null;
  for (const g of row.rel) {
    const hit = g.items.find((x) => words.some((w) => x.name.toLowerCase().includes(w)));
    if (hit) return { label: g.label, name: hit.name };
  }
  return null;
}

// What a column sorts by; a value not known sorts below every known one.
const VALUE = {
  name: (r) => r.name,
  now: (r) => r.now,
  change: (r) => (r.before == null || r.now == null ? null : r.now - r.before),
  models: (r) => r.counts?.models,
  agents: (r) => r.counts?.agents,
  installed: (r) => r.installed,
  traces: (r) => r.traces,
};

/** Rows sorted by a column ({ key, dir: "asc" | "desc" }), the most traces then the name breaking ties; unknown values last either way. */
export function sortRows(rows, { key, dir } = DEFAULT_SORT) {
  const of = VALUE[key] || VALUE.now;
  const mul = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const x = of(a);
    const y = of(b);
    if (x == null || y == null) return (x == null) - (y == null) || byNow(a, b);
    const c = key === "name" ? String(x).localeCompare(String(y)) : x - y;
    return c * mul || byNow(a, b);
  });
}

/** The sort after a header is picked: the same column the other way, or a new one, names A to Z and numbers the most first. */
export const nextSort = (sort, key) => (sort.key === key ? { key, dir: sort.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "name" ? "asc" : "desc" });
